/**
 * Notification Client Helpers
 *
 * Shared browser-side helpers for the unified notification inbox and browser
 * delivery bridge.
 *
 * Primary responsibilities:
 * - Resolve the best destination URL for a notification
 * - Decide whether a notification is unread
 * - Deduplicate browser notifications across tabs
 *
 * Dependencies:
 * - Notification entity types
 * - Shared notification href helpers
 */

import {
  resolveNotificationHref,
  resolveNotificationSpaceItemContext
} from '@/lib/notifications/href'
import type { Notification } from '@/types/notifications'

const NOTIFIED_STORAGE_KEY = 'notification-center:shown'
const MAX_TRACKED_IDS = 200

export { resolveNotificationHref, resolveNotificationSpaceItemContext }

export function isUnreadNotification(notification: Notification) {
  return (
    (notification.status === 'sent' || notification.status === 'delivered') &&
    !notification.readAt
  )
}

export function hasShownBrowserNotification(notificationId: string) {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const raw = window.localStorage.getItem(NOTIFIED_STORAGE_KEY)
    if (!raw) {
      return false
    }

    const parsed = JSON.parse(raw) as string[]
    return parsed.includes(notificationId)
  } catch {
    return false
  }
}

export function markBrowserNotificationShown(notificationId: string) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const raw = window.localStorage.getItem(NOTIFIED_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as string[]) : []
    const next = [notificationId, ...parsed.filter((id) => id !== notificationId)].slice(
      0,
      MAX_TRACKED_IDS
    )

    window.localStorage.setItem(NOTIFIED_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Ignore storage failures for notification dedupe.
  }
}
